import { Button } from '~/shadcn/ui/button/button'
import { Form } from '~/shadcn/ui/form/form'
import { Field, FieldGroup } from '~/shadcn/ui/field'
import { Spinner } from '~/shadcn/ui/spinner'
import { PhoneNumberField } from './PhoneNumberField'
import { GdprCheckBoxFields } from '~/@mid_registration/register-form/GdprCheckBoxFields'
import {
  phoneNumberWithGdprSchema,
  type PhoneNumberWithGdprSchema,
} from './phoneNumberSchema'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

type Props = {
  handleSubmitForm: (props: {
    number: string
    gdpr: boolean
    marketing: boolean
  }) => void
  isRequesting: boolean
  formContent?: React.ReactNode
  FooterComponent?: React.FC<{ children: React.ReactNode }>
  SubmitButton?: React.ReactNode
  error?: string
  children?: React.ReactNode | React.ReactNode[]
}

function PhoneNumberFormWithGdpr({
  handleSubmitForm,
  isRequesting,
  formContent,
  FooterComponent,
  SubmitButton,
  error,
  children,
}: Props) {
  const submitButton =
    SubmitButton ??
    (() => (
      <div className="flex flex-col-reverse justify-center lg:flex-row mt-4 gap-4 w-full">
        <Button
          className="w-full"
          variant="accent"
          type="submit"
          disabled={isRequesting}>
          {isRequesting && <Spinner className="size-4" />} {` Overiť`}
        </Button>
      </div>
    ))()

  const footer = (() => {
    if (FooterComponent)
      return <FooterComponent>{submitButton}</FooterComponent>

    return (
      <FieldGroup>
        <Field>{submitButton}</Field>
      </FieldGroup>
    )
  })()

  const form = useForm<PhoneNumberWithGdprSchema>({
    resolver: zodResolver(phoneNumberWithGdprSchema),
    defaultValues: {
      prefix: '+421',
      number: '',
      gdpr: false,
      marketing: false,
    },
  })

  function handleSubmitFormInternal({
    prefix,
    number,
    gdpr,
    marketing,
  }: PhoneNumberWithGdprSchema) {
    handleSubmitForm({ number: `${prefix}${number}`, gdpr, marketing })
  }

  const errors = (() =>
    Object.values(form.formState.errors).map((error) => ({
      message: error?.message,
    })) ?? [])()

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmitFormInternal)}>
        <FieldGroup>
          {formContent}
          <Field className="flex flex-col gap-2">
            <PhoneNumberField
              aria-invalid={errors.length > 0 || !!error}
              control={form.control}
              isRequesting={isRequesting}
              error={form.formState.errors.number?.message}
            />

            <Field className="mt-6">
              <GdprCheckBoxFields
                control={form.control}
                isRequesting={isRequesting}
              />
            </Field>

            {children}

            {footer}
          </Field>
        </FieldGroup>
      </form>
    </Form>
  )
}

export { PhoneNumberFormWithGdpr }
